/**
 * Icon component - wrapper for Phosphor icons.
 * Provides a consistent interface for rendering icons with proper accessibility.
 * @module components/ui/Icon
 */

import { type HTMLAttributes } from "react";

import { GLYPH_SIZE_CLASS, type GlyphSize } from "./Text";

export interface IconProps extends HTMLAttributes<HTMLElement> {
  /** The Phosphor icon name (without 'ph-' prefix), e.g., "plus", "trash", "warning" */
  name: string;
  /**
   * Icon weight.
   *
   * Only the three weights `src/index.tsx` imports. Phosphor ships six, but a
   * weight whose font is not loaded renders an empty box — silently, and
   * invisibly to jsdom, which has no fonts. Adding one here means adding its
   * `@phosphor-icons/web/<weight>` import, and its font, to every page.
   */
  weight?: "regular" | "bold" | "fill";
  /**
   * Glyph size from the type scale. A glyph is sized by font size, so this is
   * the one place a screen reaches for `text-lg` or `text-xl`. Omitted, it
   * inherits the surrounding text.
   */
  size?: GlyphSize;
  /** Additional CSS classes */
  className?: string;
  /** Whether the icon is decorative (hidden from screen readers). Defaults to true. */
  decorative?: boolean;
  /** Accessible label for non-decorative icons */
  label?: string;
}

/**
 * Icon component that renders Phosphor icons.
 *
 * @example
 * ```tsx
 * // Decorative icon (default)
 * <Icon name="plus" />
 *
 * // Bold weight
 * <Icon name="trash" weight="bold" />
 *
 * // Accessible icon with label
 * <Icon name="warning" decorative={false} label="Warning" />
 * ```
 */
export function Icon({
  name,
  weight = "regular",
  size = undefined,
  className = "",
  decorative = true,
  label = undefined,
  ...props
}: IconProps) {
  const weightClass = weight === "regular" ? "ph" : `ph-${weight}`;
  const sizeClass = size ? GLYPH_SIZE_CLASS[size] : "";
  const iconClass = [weightClass, `ph-${name}`, sizeClass, className].filter(Boolean).join(" ");

  return (
    <i
      className={iconClass}
      aria-hidden={decorative}
      aria-label={!decorative ? label : undefined}
      role={!decorative ? "img" : undefined}
      {...props}
    />
  );
}

export default Icon;
